import { z } from "zod";

export const appointmentTimeSlot = z
  .object({
    startDate: z.string(),
    endDate: z.string(),
  })
  .optional();

export const cancellationReason = z.enum([
  "NOSS_FAILED_BMT",
  "NOSS_FAILED_BMK",
  "userRequest",
  "agentRequest",
  "draftExpired",
  "paymentError",
  "unsuccessfulDelivery",
  "installmentExpired",
  "installmentNegative",
  "installmentError",
  "fulfillmentError",
  "fulfillmentExpired",
]);

export const state = z.enum([
  "START",
  "CONTRACT_VERIFICATION",
  "IDENTITY",
  "IDENTITY_ERROR",
  "IDENTITY_PENDING",
  "IDENTITY_FULLSTOP",
  "PAYMENT_METHOD",
  "APPOINTMENT",
  "CONSENTS",
  "ORDER_SUMMARY",
  "ORDER_PAYMENT",
  "ORDER_PAYMENT_ERROR",
  "ORDER_FULFILLMENT",
  "ORDER_HELD",
  "ORDER_FINISHING",
  "ORDER_PENDING_CANCELLATION",
  "ORDER_COMMITTED",
  "COMPLETED",
]);

export const paymentErrorCategory = z.enum([
  "DEFAULT",
  "CARDLIMIT",
  "CARDERROR",
  "BANKDECLINED",
  "PIN",
  "DW_LIMIT_EXCEEDED",
  "CARDBLOCKED",
]);

const actionsRel = z.enum([
  "CONSENTS_SET",
  "ORDER_COMMIT",
  "CONTRACT_VERIFICATION_SETUP",
  "CONTRACT_VERIFICATION_CHECK",
  "TECHNICAL_CONTRACT_VERIFICATION_SETUP",
  "TECHNICAL_CONTRACT_VERIFICATION_CHECK",
  "ORDER_CANCEL",
  "ORDER_CREATE",
  "ORDER_PAYMENT_REPEAT",
  "APPOINTMENT_SLOT_CHANGE",
  "TECHNICAL_IDENTITY_CHECK",
  "IDENTITY_CHECK",
  "PAYMENT_METHOD_SET",
  "IDENTITY_SETUP",
  "APPOINTMENT_SLOT_RESERVE",
  "APPOINTMENT_SLOT_CHANGE_FULFILLMENT",
  "REACTIVATION_ORDER_COMMIT",
  "VOUCHER_SET",
]);

export const actions = z.array(
  z.object({
    href: z.string(),
    method: z.string(),
    rel: actionsRel,
  }),
);

export const paymentStatus = z.enum([
  "PENDING_REPEATING",
  "COMPLETED",
  "PENDING_REPEATING_RETRY",
  "NEW",
  "FAILED",
  "PARTIAL",
  "PENDING",
  "CANCELLED",
]);

export const identityMethodId = z.enum([
  "MOJEID",
  "ONFIDO",
  "TECHNICALPARTNER",
  "SALESAGENT",
]);

const orderSummary = z.object({
  totalOrderPrice: z.number(),
  totalRecurringOrderPrice: z.number(),
  totalOneTimeOrderPrice: z.number(),
  totalRecurringOrderPriceAfterPromotion: z.number().optional(),
  items: z.array(
    z
      .object({
        offerId: z.union([
          z.literal("NJU_FTTH_Subscription"),
          z.literal("P-NJU-Internet"),
        ]),
        offerPrice: z.number(),
        offerPriceType: z.literal("RECURRING"),
      })
      .or(
        z.object({
          offerId: z.union([
            z.literal("NJU_FTTH_NEOIASSYSKONF"),
            z.literal("P-NJU-Assistance"),
            z.literal("P-NJU-FTTH-Activation-Payment"),
          ]),
          offerPrice: z.number(),
          offerPriceType: z.literal("ONE-TIME"),
        }),
      )
      .or(
        z.object({
          offerId: z.union([z.literal("NJU_FTTH_SFH"), z.literal("P-NJU-SFH")]),
          offerPrice: z.number(),
          offerPriceType: z.literal("RECURRING"),
        }),
      ),
  ),
});

const installationAddress = z
  .object({
    installationStandard: z.string(),
    isSfh: z.boolean(),
  })
  .optional();

export const ftthOrchestrator = z.object({
  type: z.literal("NJU_FTTH_ONBOARDING"),
  state,
  actions,
  identityMethodId: identityMethodId.optional(),
  salesAgentLock: z.boolean().optional(),
  paymentStatus: paymentStatus.optional(),
  cancellationReason: cancellationReason.optional(),
  appointmentTimeSlot: appointmentTimeSlot.optional(),
  id: z.string(),
  applicantId: z.string().optional(),
  userId: z.string(),
  verificationStatusReason: z.string().optional(),
  onfidoVerificationExceeded: z.boolean().optional(),
  paymentTimestamp: z.string().optional(),
  paymentStartDate: z.string().optional(),
  njuCustomerMsisdn: z.string().optional(),
  orderId: z.string().optional(),
  hasConsentsSet: z.boolean().optional(),
  hasPaymentMethodStarted: z.boolean().optional(),
  hasIdentityStarted: z.boolean().optional(),
  isUserVerified: z.boolean().optional(),
  installationToken: z.string().optional(),
  installationAddress,
  primaryPaymentMethodId: z.string().optional(),
  note: z.string().optional(),
  contactNumber: z.string().optional(),
  paymentErrorCategory: paymentErrorCategory.optional(),
  offerIds: z.array(z.string()),
  orderSummary: orderSummary.optional(),
  orderStatus: z.string().optional(),
  userVerificationStatus: z.string().optional(),
  identitySetupLock: z.boolean().optional(),
  processId: z.string().optional(),
  voucherCode: z.string().optional(),
  voucherRewardDescription: z.string().optional(),
  voucherPromoCode: z.string().optional(),
});

type IFtthOrchestratorAPI = z.infer<typeof ftthOrchestrator>;

export type IFtthOrchestrator = IFtthOrchestratorAPI & {
  contactNumberWithoutCountyCode?: string;
  offerId?: string;
};
